import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from '../../../_shared/Spinner';

interface Property {
	id: number;
	title: string;
	lat?: number;
	lng?: number;
	permalink: string;
	image: string;
	location: string;
	guests: number;
	bedrooms: number;
	bathrooms: number;
	hostawayId: string;
	basePrice: number;
	currency: string;
	// Server-rendered by ocean_render_property_card() (php/property-card.php)
	// -- the same markup the list view uses via SearchResults.tsx, reused
	// as-is for the marker click popup rather than building a second card
	// layout just for the map.
	cardHtml: string;
}

interface MapViewProps {
	properties: Property[];
	onBoundsChange?: (visiblePropertyIds: number[]) => void;
	apiKey: string;
}

declare global {
	interface Window {
		google: any;
	}
}

export const MapView = ({ properties, onBoundsChange, apiKey }: MapViewProps) => {
	const mapRef = useRef<HTMLDivElement>(null);
	const markersRef = useRef<any[]>([]);
	// One shared InfoWindow, repositioned/re-filled per marker click, rather
	// than one per marker -- Google Maps only shows one at a time anyway.
	const infoWindowRef = useRef<any>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Filter properties that have location data
	const propertiesWithLocation = properties.filter(p => p.lat && p.lng);

	useEffect(() => {
		if (!apiKey) {
			setError('Google Maps API key not configured');
			setIsLoading(false);
			return;
		}

		if (propertiesWithLocation.length === 0) {
			setError('No properties with location data available');
			setIsLoading(false);
			return;
		}

		// Load Google Maps API if not already loaded
		if (typeof window.google === 'undefined' || typeof window.google.maps === 'undefined') {
			const script = document.createElement('script');
			const callbackName = `initPropertyResultsMap_${Math.random().toString(36).substring(7)}`;
			
			(window as any)[callbackName] = () => {
				initMap();
				delete (window as any)[callbackName];
			};

			script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}`;
			script.async = true;
			script.defer = true;
			script.onerror = () => {
				setError('Failed to load Google Maps');
				setIsLoading(false);
			};
			document.head.appendChild(script);
		} else {
			initMap();
		}
	}, [propertiesWithLocation.length, apiKey]);

	const initMap = () => {
		if (!mapRef.current) return;

		try {
			// Calculate center based on all properties
			const avgLat = propertiesWithLocation.reduce((sum, p) => sum + (p.lat || 0), 0) / propertiesWithLocation.length;
			const avgLng = propertiesWithLocation.reduce((sum, p) => sum + (p.lng || 0), 0) / propertiesWithLocation.length;

			const mapOptions = {
				zoom: 10,
				center: { lat: avgLat, lng: avgLng },
				mapTypeId: 'roadmap',
			};

			const map = new window.google.maps.Map(mapRef.current, mapOptions);
			const bounds = new window.google.maps.LatLngBounds();
			infoWindowRef.current = new window.google.maps.InfoWindow({
				// Lifts the popup clear of the marker graphic so the card sits
				// centred above the pin it belongs to. The anchor point is the
				// marker's base, and the default pin is 32px tall, so the
				// offset has to clear that plus a few px of breathing room.
				// disableAutoPan isn't set (defaults to false), so Maps
				// auto-pans to keep the raised card in view.
				pixelOffset: new window.google.maps.Size(0, -36),
			});

			// Clear existing markers
			markersRef.current.forEach(marker => marker.setMap(null));
			markersRef.current = [];

			const pinColors = [
				'red', 'blue', 'green', 'yellow', 'purple',
				'orange', 'pink', 'ltblue'
			];

			propertiesWithLocation.forEach((property, index) => {
				const position = { lat: property.lat!, lng: property.lng! };
				const color = pinColors[index % pinColors.length];

				const marker = new window.google.maps.Marker({
					position,
					map,
					title: property.title,
					animation: window.google.maps.Animation.DROP,
					icon: `http://maps.google.com/mapfiles/ms/icons/${color}-dot.png`
				});

				bounds.extend(position);

				// Reuse the same card the list view renders (property.cardHtml,
				// server-rendered) as the popup content -- see the Property
				// interface above.
				marker.addListener('click', () => {
					if (!infoWindowRef.current) return;
					// property-list__card as well as the popup's own class:
					// ocean_render_property_card() emits the card's contents but
					// not its wrapper, and the hover/focus rules that draw the
					// CTA underline (property-list.scss) key off that class on
					// the wrapper -- the grid and carousel supply their own.
					infoWindowRef.current.setContent(`<div class="property-results__map-card property-list__card">${property.cardHtml}</div>`);
					infoWindowRef.current.open({ map, anchor: marker });
				});

				markersRef.current.push(marker);
			});

			if (propertiesWithLocation.length > 1) {
				map.fitBounds(bounds);
			}

			// Add bounds change listener to filter properties
			const updateVisibleProperties = () => {
				if (!onBoundsChange) return;
				
				const mapBounds = map.getBounds();
				if (!mapBounds) return;

				const visibleIds = propertiesWithLocation
					.filter(property => {
						const position = new window.google.maps.LatLng(property.lat!, property.lng!);
						return mapBounds.contains(position);
					})
					.map(property => property.id);

				onBoundsChange(visibleIds);
			};

			// Call initially after map loads
			setTimeout(updateVisibleProperties, 100);

			// Listen for bounds changes (pan, zoom)
			map.addListener('bounds_changed', updateVisibleProperties);

			setIsLoading(false);
		} catch (err) {
			setError('Failed to initialize map');
			setIsLoading(false);
		}
	};

	if (error) {
		return <div className="property-results__map-error">{error}</div>;
	}

	return (
		<div className="property-results__map-wrapper">
			{isLoading && <Spinner className="property-results__map-loading" />}
			<div ref={mapRef} className="property-results__map" style={{ width: '100%', height: '100%', minHeight: '600px' }} />
		</div>
	);
};
